import { useState, useEffect, useCallback, useRef } from 'react';
import {
  collection as firestoreCollection,
  query,
  where,
  orderBy as firestoreOrderBy,
  limit,
  getDocs,
  onSnapshot,
  QueryConstraint,
  DocumentData,
  Unsubscribe,
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import type { FirestoreWhereFilter } from '../types/firebase';

interface UseFirestoreCollectionOptions {
  collection: string;
  where?: FirestoreWhereFilter[];
  orderBy?: [string, 'asc' | 'desc'];
  limitCount?: number;
  realtime?: boolean;
}

interface UseFirestoreCollectionResult<T> {
  data: T[];
  loading: boolean;
  error: Error | null;
  refetch: () => void;
}

export function useFirestoreCollection<T extends DocumentData>(
  options: UseFirestoreCollectionOptions
): UseFirestoreCollectionResult<T> {
  const { collection: collectionName, where: whereFilters, orderBy: orderByField, limitCount, realtime = false } = options;

  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const unsubscribeRef = useRef<Unsubscribe | null>(null);

  const buildQuery = useCallback(() => {
    const constraints: QueryConstraint[] = [];

    if (whereFilters) {
      for (const [field, op, value] of whereFilters) {
        constraints.push(where(field, op, value));
      }
    }

    if (orderByField) {
      constraints.push(firestoreOrderBy(orderByField[0], orderByField[1]));
    }

    if (limitCount) {
      constraints.push(limit(limitCount));
    }

    return query(firestoreCollection(db, collectionName), ...constraints);
  }, [collectionName, JSON.stringify(whereFilters), JSON.stringify(orderByField), limitCount]);

  const processSnapshot = useCallback((snapshot: { docs: DocumentData[] }) => {
    const items = snapshot.docs.map((doc: DocumentData) => ({
      id: doc.id,
      ...doc.data(),
    })) as T[];
    setData(items);
    setLoading(false);
    setError(null);
  }, []);

  const fetchData = useCallback(() => {
    setLoading(true);
    setError(null);

    // Cleanup previous subscription
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }

    const q = buildQuery();

    if (realtime) {
      unsubscribeRef.current = onSnapshot(
        q,
        (snapshot) => processSnapshot(snapshot),
        (err) => {
          setError(err);
          setLoading(false);
        }
      );
    } else {
      getDocs(q)
        .then((snapshot) => processSnapshot(snapshot))
        .catch((err) => {
          setError(err);
          setLoading(false);
        });
    }
  }, [buildQuery, realtime, processSnapshot]);

  useEffect(() => {
    fetchData();

    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
    };
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
}
